/**
 * OCC deep-link reader (2026-08-09 · OCC deep-linking contract)
 * ---------------------------------------------------------------
 * Reads query-string parameters from `window.location.hash` and exposes
 * them to any workspace so it can pre-filter its view when the operator
 * arrives from the Operations Command Centre.
 *
 * Every OCC card emits a hash of the form:
 *   #<route>?queue=<queue>&<filter…>&focus=<anchor>&_from=occ
 *
 * The receiving workspace consumes it via:
 *   const { fromOcc, queue, queueLabel, params, focus, exit } = useOccContext();
 *
 * `queueLabel` is derived on the receiver side via the registry so we
 * do not need to trust the hash payload for user-visible copy.
 */

import { useEffect, useState, useCallback } from "react";
import REGISTRY from "@/config/occTiles";

const QUEUE_LABELS = Object.freeze(
  Object.values(REGISTRY).reduce((acc, entry) => {
    acc[entry.queue] = entry.queue_label;
    return acc;
  }, {})
);

function parseHash(hash) {
  const raw = (hash || "").replace(/^#/, "");
  const qIdx = raw.indexOf("?");
  if (qIdx < 0) return { route: raw, params: {} };
  const route = raw.slice(0, qIdx);
  const params = {};
  new URLSearchParams(raw.slice(qIdx + 1)).forEach((v, k) => { params[k] = v; });
  return { route, params };
}

export function useOccContext() {
  const [state, setState] = useState(() => parseHash(typeof window !== "undefined" ? window.location.hash : ""));

  useEffect(() => {
    const onHash = () => setState(parseHash(window.location.hash));
    window.addEventListener("hashchange", onHash);
    return () => window.removeEventListener("hashchange", onHash);
  }, []);

  const { route, params } = state;
  const fromOcc = params._from === "occ";
  const queue = params.queue || null;
  const queueLabel = queue ? (QUEUE_LABELS[queue] || null) : null;
  const focus = params.focus || null;

  const exit = useCallback(() => {
    try { window.location.hash = "#command-centre"; } catch (_e) { /* ignore */ }
  }, []);

  return { route, fromOcc, queue, queueLabel, params, focus, exit };
}

/**
 * Focus helper — call this once the receiving workspace has finished
 * rendering. It looks up the element with `id={focus}` (or a
 * `data-occ-focus="<focus>"` attribute) and scrolls it into view with a
 * short highlight ring. Idempotent per hash-change.
 */
export function scrollFocus(focusKey) {
  if (!focusKey || typeof document === "undefined") return;
  const el =
    document.getElementById(focusKey) ||
    document.querySelector(`[data-occ-focus="${focusKey}"]`);
  if (!el) return;
  try {
    el.scrollIntoView({ behavior: "smooth", block: "start" });
    el.classList.add("cc-occ-focus-ring");
    window.setTimeout(() => el.classList.remove("cc-occ-focus-ring"), 2200);
  } catch (_e) { /* ignore */ }
}
